// src/pages/TripDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTripById, getTripPlaces, addPlaceToTrip, updateTrip } from '../services/tripService';
import CalendarScheduler from '../components/CalendarScheduler';
import TripSummary from '../components/TripSummary';
import PlaceForm from '../components/PlaceForm';
import TripRating from '../components/TripRating';
import TripComments from '../components/TripComments';
import './TripDetail.css';

const TripDetail = ({ user }) => {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPlaceForm, setShowPlaceForm] = useState(false);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    fetchTripData();
  }, [tripId]);

  const fetchTripData = async () => {
    try {
      setLoading(true);
      const [tripData, placesData] = await Promise.all([
        getTripById(tripId),
        getTripPlaces(tripId)
      ]);

      // 檢查權限：可以查看自己的行程或公開的行程
      if (tripData.uid !== user.uid && !tripData.isPublic) {
        navigate('/');
        return;
      }

      setTrip(tripData);
      setPlaces(placesData);
    } catch (error) {
      setError('無法載入行程資料');
      console.error('Error fetching trip data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPlace = async (placeData) => {
    try {
      await addPlaceToTrip(tripId, placeData);
      await fetchTripData(); // 重新載入資料
      setShowPlaceForm(false);
    } catch (error) {
      console.error('Error adding place:', error);
      alert('新增景點失敗');
    }
  };

  const handleUpdateTrip = async (updates) => {
    try {
      await updateTrip(tripId, updates);
      setTrip(prev => ({ ...prev, ...updates }));
    } catch (error) {
      console.error('Error updating trip:', error);
      alert('更新行程失敗');
    }
  };

  const calculateTotalCost = () => {
    const totals = {
      food: 0,
      accommodation: 0,
      transport: 0,
      entertainment: 0,
      other: 0
    };

    places.forEach(place => {
      if (place.expense) {
        Object.keys(totals).forEach(key => {
          totals[key] += place.expense[key] || 0;
        });
      }
    });

    return totals;
  };

  const calculateAverageRating = () => {
    const ratedPlaces = places.filter(place => place.rating && place.rating > 0);
    if (ratedPlaces.length === 0) return 0;
    const sum = ratedPlaces.reduce((total, place) => total + Number(place.rating), 0);
    return (sum / ratedPlaces.length).toFixed(1);
  };

  if (loading) {
    return (
      <div className="trip-detail-container">
        <div className="loading">載入中...</div>
      </div>
    );
  }

  if (error || !trip) {
    return (
      <div className="trip-detail-container">
        <div className="error-message">{error || '找不到行程資料'}</div>
      </div>
    );
  }

  return (
    <div className="trip-detail-container">
      <div className="trip-header">
        <div className="trip-header-content">
          <h1>{trip.title}</h1>
          <p className="trip-location">{trip.country} - {trip.city}</p>
          <p className="trip-dates">
            {new Date(trip.start_date).toLocaleDateString('zh-TW')} - 
            {new Date(trip.end_date).toLocaleDateString('zh-TW')}
          </p>
        </div>
        
        <div className="trip-actions">
          {/* 只有行程擁有者才能編輯 */}
          {trip.uid === user.uid && !trip.isCompleted && (
            <>
              <button 
                onClick={() => setEditMode(!editMode)}
                className="edit-btn"
              >
                {editMode ? '完成編輯' : '編輯模式'}
              </button>
              <button 
                onClick={() => setShowPlaceForm(true)}
                className="add-place-btn"
              >
                新增景點
              </button>
            </>
          )}
          
          {/* 只有行程擁有者才能發佈/取消發佈 */}
          {trip.uid === user.uid && (
            <button 
              onClick={() => handleUpdateTrip({ isPublic: !trip.isPublic })}
              className="public-btn"
            >
              {trip.isPublic ? '設為私人' : '發佈行程'}
            </button>
          )}
        </div>
      </div>

      <div className="trip-content">
        <div className="trip-main">
          <CalendarScheduler 
            trip={trip}
            places={places}
            editMode={editMode && trip.uid === user.uid} // 只有擁有者才能編輯
            onUpdate={fetchTripData}
            isOwner={trip.uid === user.uid} // 傳遞擁有者狀態
          />
          
          {/* 留言板（只有公開行程才顯示） */}
          {trip.isPublic && (
            <TripComments 
              trip={trip}
              user={user}
            />
          )}
        </div>
        
        <div className="trip-sidebar">
          <TripSummary 
            trip={trip}
            places={places}
            totalCost={calculateTotalCost()}
            averageRating={calculateAverageRating()}
          />
          
          {/* 行程評分組件（所有用戶都可以評分） */}
          {trip.isPublic && (
            <TripRating 
              trip={trip}
              user={user}
            />
          )}
        </div>
      </div>

      {/* 只有行程擁有者才能新增景點 */}
      {showPlaceForm && trip.uid === user.uid && (
        <PlaceForm 
          trip={trip}
          onSubmit={handleAddPlace}
          onCancel={() => setShowPlaceForm(false)}
        />
      )}
    </div>
  );
};

export default TripDetail;